import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Checkbox } from "@/components/podkit/checkbox/Checkbox";
import { Label } from "@/components/podkit/forms/Label";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useCreatePrompt } from "@/queries/prompt-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";

type DefaultCommand = {
    id: string;
    name: string;
    command: string;
    description: string;
    prompt: string;
};

const DEFAULT_COMMANDS: DefaultCommand[] = [
    {
        id: "pr",
        name: "Pull Request",
        command: "pull-request",
        description: "Raise a draft pull request with a standard template",
        prompt: "Raise a draft PR for a branch starting with my initials following this template. Get my initials from the configured git username. Check for manual files changes before creating the PR description. \n## Description\n<!-- Describe your changes in detail -->\n## Related Issue(s)\n<!-- List the issue(s) this PR solves -->\nFixes <issue-ref>\n## How to test\n<!-- Provide steps to test this PR -->",
    },
    {
        id: "documentation",
        name: "Documentation",
        command: "docs",
        description: "Generate documentation for functions and code",
        prompt: "Generate comprehensive documentation for the following code, including:\n- Function/method descriptions\n- Parameter explanations\n- Return value descriptions\n- Usage examples",
    },
    {
        id: "explain-code",
        name: "Explain Code",
        command: "explain",
        description: "Explain how code works in simple terms",
        prompt: "Explain how this code works in simple, easy-to-understand terms. Break down complex concepts and provide examples where helpful.",
    },
    {
        id: "catch-up",
        name: "Catch Up",
        command: "catch-up",
        description: "Makes Ona understand the changes in this environment compared to main",
        prompt: "Understand the changes in this environment compared to main. What files have changed and why? What's the user's intent?",
    },
];

type ImportDefaultCommandsDialogProps = {
    open: boolean;
    onClose: () => void;
};

export const ImportDefaultCommandsDialog: FC<ImportDefaultCommandsDialogProps> = ({ open, onClose }) => {
    const [selectedCommands, setSelectedCommands] = useState<Set<string>>(
        new Set(DEFAULT_COMMANDS.map((cmd) => cmd.id)),
    );
    const [isImporting, setIsImporting] = useState(false);

    const createPrompt = useCreatePrompt();
    const { toast } = useToast();

    const toggleCommand = useCallback((commandId: string) => {
        setSelectedCommands((prev) => {
            const next = new Set(prev);
            if (next.has(commandId)) {
                next.delete(commandId);
            } else {
                next.add(commandId);
            }
            return next;
        });
    }, []);

    const toggleAll = useCallback(() => {
        if (selectedCommands.size === DEFAULT_COMMANDS.length) {
            setSelectedCommands(new Set());
        } else {
            setSelectedCommands(new Set(DEFAULT_COMMANDS.map((cmd) => cmd.id)));
        }
    }, [selectedCommands.size]);

    const handleImport = useCallback(async () => {
        if (selectedCommands.size === 0) {
            return;
        }

        setIsImporting(true);
        const commandsToImport = DEFAULT_COMMANDS.filter((cmd) => selectedCommands.has(cmd.id));

        let importedCount = 0;

        // Import commands sequentially to avoid race conditions
        for (const cmd of commandsToImport) {
            try {
                await createPrompt.mutateAsync({
                    name: cmd.name,
                    description: cmd.description,
                    prompt: cmd.prompt,
                    isCommand: true,
                    command: cmd.command,
                });
                importedCount++;
            } catch (error) {
                // Silently skip commands that already exist
                const errorMessage = formatError(error);
                const isDuplicate =
                    errorMessage.toLowerCase().includes("already exists") ||
                    errorMessage.toLowerCase().includes("unique");
                if (!isDuplicate) {
                    console.error(`Failed to import ${cmd.name}:`, error);
                }
            }
        }

        setIsImporting(false);

        if (importedCount > 0) {
            toast({
                title: "Commands imported successfully",
                description: `${importedCount} command${importedCount > 1 ? "s" : ""} imported`,
            });
        }

        onClose();
    }, [selectedCommands, createPrompt, toast, onClose]);

    const allSelected = selectedCommands.size === DEFAULT_COMMANDS.length;
    const someSelected = selectedCommands.size > 0 && selectedCommands.size < DEFAULT_COMMANDS.length;

    return (
        <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="import-default-commands-modal"
                data-track-location={TrackLocations.ImportDefaultCommandsDialog}
            >
                <DialogHeader>
                    <DialogTitle>Import Default Commands</DialogTitle>
                    <DialogDescription>
                        Select the default commands you want to import into your organization
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="space-y-4">
                    <div className="flex items-center space-x-2 border-b border-border-base pb-3">
                        <Checkbox
                            id="select-all"
                            checked={someSelected ? "indeterminate" : allSelected}
                            onCheckedChange={toggleAll}
                            data-testid="select-all-checkbox"
                        />
                        <Label htmlFor="select-all" className="cursor-pointer font-medium">
                            Select All
                        </Label>
                    </div>

                    <div className="max-h-[400px] space-y-3 overflow-y-auto">
                        {DEFAULT_COMMANDS.map((cmd) => (
                            <div
                                key={cmd.id}
                                className="flex items-start space-x-3 rounded-lg border border-border-base p-3 hover:bg-surface-hover"
                            >
                                <Checkbox
                                    id={cmd.id}
                                    checked={selectedCommands.has(cmd.id)}
                                    onCheckedChange={() => toggleCommand(cmd.id)}
                                    data-testid={`command-checkbox-${cmd.id}`}
                                    className="mt-1"
                                />
                                <div className="flex-1 space-y-1">
                                    <Label htmlFor={cmd.id} className="cursor-pointer font-medium">
                                        {cmd.name}
                                        <span className="ml-2 text-sm font-normal text-content-muted">
                                            /{cmd.command}
                                        </span>
                                    </Label>
                                    <p className="text-sm text-content-secondary">{cmd.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" disabled={isImporting}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={handleImport}
                        disabled={selectedCommands.size === 0 || isImporting}
                        loading={isImporting}
                        data-testid="import-commands-button"
                        data-tracking-id="import-default-commands"
                    >
                        Import{selectedCommands.size > 0 && <span> ({selectedCommands.size})</span>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
